import { z } from 'zod';
import { CONDITION_OPERATORS, CRM_MODULES, type CrmModule } from '../enums.js';
import { optionalText, paginationQuerySchema, uuidSchema } from './common.js';
import type { ReportField } from '../reporting/catalogue.js';
import { businessManagerFilterSchema, type BusinessManagerRef } from './records.js';

/**
 * Custom reports.
 *
 * A report is a saved *question*: which module, which columns, which rows,
 * grouped how, summarised how, drawn as what. Running it is a separate step,
 * so the same definition answers today's question today and next month's next
 * month.
 *
 * The definition is stored as one validated JSON document rather than being
 * spread across tables. It is read and written whole and never queried by its
 * parts, which is exactly when a document beats a schema — and the Zod schema
 * below is what keeps it from being a dumping ground.
 */

/** `CHANGED` belongs to workflow triggers: a report looks at rows, not edits. */
export const REPORT_OPERATORS = CONDITION_OPERATORS.filter(
  (operator) => operator !== 'CHANGED',
) as unknown as readonly Exclude<(typeof CONDITION_OPERATORS)[number], 'CHANGED'>[];

export const REPORT_AGGREGATIONS = ['count', 'sum', 'avg', 'min', 'max'] as const;
export type ReportAggregation = (typeof REPORT_AGGREGATIONS)[number];

export const REPORT_CHART_TYPES = ['none', 'bar', 'line', 'pie', 'donut'] as const;
export type ReportChartType = (typeof REPORT_CHART_TYPES)[number];

/** The most rows one run may read. A report is a summary, not an extract. */
export const REPORT_ROW_LIMIT = 5000;

export const reportFilterSchema = z.object({
  field: z.string().trim().min(1).max(64),
  operator: z.enum(REPORT_OPERATORS as unknown as [string, ...string[]]),
  /**
   * Compared as text, a number, a date or a list depending on the field's
   * declared type — the runner decides, not the client, because the client
   * cannot be trusted to have read the catalogue.
   */
  value: z.union([z.string().max(500), z.number(), z.boolean(), z.array(z.string().max(200)).max(50)]).optional(),
});
export type ReportFilter = z.infer<typeof reportFilterSchema>;

export const reportColumnSchema = z.object({
  field: z.string().trim().min(1).max(64),
  /** Overrides the catalogue's label, for a report that speaks the team's dialect. */
  label: optionalText(80),
});
export type ReportColumn = z.infer<typeof reportColumnSchema>;

export const reportAggregationSchema = z.object({
  fn: z.enum(REPORT_AGGREGATIONS),
  /** Omitted for `count`, which counts rows rather than values. */
  field: z.string().trim().min(1).max(64).optional(),
  label: optionalText(80),
});
export type ReportAggregationSpec = z.infer<typeof reportAggregationSchema>;

export const reportDefinitionSchema = z
  .object({
    /** One of the two is set; the refinement below enforces it. */
    module: z.enum(CRM_MODULES).optional(),
    customModuleId: uuidSchema.optional(),
    columns: z.array(reportColumnSchema).max(30).default([]),
    filters: z.array(reportFilterSchema).max(20).default([]),
    /** A single field. Two levels of grouping is a pivot table, not this. */
    groupBy: z.string().trim().min(1).max(64).nullish(),
    aggregations: z.array(reportAggregationSchema).max(10).default([]),
    chart: z.enum(REPORT_CHART_TYPES).default('none'),
    sortBy: z.string().trim().min(1).max(64).nullish(),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(REPORT_ROW_LIMIT).default(500),
  })
  .refine((value) => Boolean(value.module) !== Boolean(value.customModuleId), {
    message: 'Choose one module',
    path: ['module'],
  })
  .refine(
    (value) => value.columns.length > 0 || value.aggregations.length > 0,
    // A report with neither returns rows of nothing.
    { message: 'Pick at least one column, or something to summarise', path: ['columns'] },
  )
  .refine(
    (value) => value.aggregations.every((item) => item.fn === 'count' || Boolean(item.field)),
    { message: 'Say which field to summarise', path: ['aggregations'] },
  );
export type ReportDefinition = z.infer<typeof reportDefinitionSchema>;

export const createSavedReportSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(120),
  description: optionalText(500),
  /**
   * Shared reports are visible to everyone who may read reports; the rows
   * inside are still scoped per viewer, so sharing a report never shares the
   * records behind it.
   */
  isShared: z.coerce.boolean().default(false),
  definition: reportDefinitionSchema,
  businessManagerId: uuidSchema.optional(),
});
export type CreateSavedReportInput = z.infer<typeof createSavedReportSchema>;

export const updateSavedReportSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: optionalText(500),
  isShared: z.coerce.boolean().optional(),
  definition: reportDefinitionSchema.optional(),
  businessManagerId: uuidSchema.optional(),
});
export type UpdateSavedReportInput = z.infer<typeof updateSavedReportSchema>;

export const listSavedReportsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(['name', 'createdAt', 'updatedAt']).default('updatedAt'),
  module: z.enum(CRM_MODULES).optional(),
  businessManagerId: businessManagerFilterSchema.optional(),
});
export type ListSavedReportsQuery = z.infer<typeof listSavedReportsQuerySchema>;

/** Running an unsaved definition, so the builder can preview before saving. */
export const runReportSchema = z.object({ definition: reportDefinitionSchema });
export type RunReportInput = z.infer<typeof runReportSchema>;

export const REPORT_EXPORT_FORMATS = ['csv', 'pdf'] as const;
export type ReportExportFormat = (typeof REPORT_EXPORT_FORMATS)[number];

export const reportExportQuerySchema = z.object({
  format: z.enum(REPORT_EXPORT_FORMATS).default('csv'),
});
export type ReportExportQuery = z.infer<typeof reportExportQuerySchema>;

// --- Responses ------------------------------------------------------------

export interface SavedReportSummary {
  id: string;
  name: string;
  description: string | null;
  module: CrmModule | null;
  customModuleId: string | null;
  /** What the report is of, in words: "Deals", "Projects". */
  sourceLabel: string;
  chart: ReportChartType;
  isShared: boolean;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface SavedReportDetail extends SavedReportSummary {
  definition: ReportDefinition;
}

/** One column of a result, resolved against the catalogue. */
export interface ReportResultColumn {
  field: string;
  label: string;
  type: ReportField['type'];
}

export interface ReportGroupRow {
  /** The group's value, already rendered as text. `—` when the field is empty. */
  key: string;
  count: number;
  /** Keyed by `${fn}:${field}`, matching the definition's aggregations. */
  values: Record<string, string>;
}

export interface ReportResult {
  columns: ReportResultColumn[];
  rows: Record<string, string>[];
  /** Present when the definition groups. */
  groups: ReportGroupRow[] | null;
  /** Organization-wide totals for the aggregations, over every matching row. */
  totals: Record<string, string>;
  /** Rows actually read; `limit` caps it, and `truncated` says when it bit. */
  rowCount: number;
  truncated: boolean;
  chart: ReportChartType;
  /** The currency money columns are in, for the client's formatting. */
  currency: string;
  runAt: string;
}
